import apiService from "@/api-service";
import { Bestek } from "@/models/bestek";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from './bestek_detail.module.scss';
import { IconContext } from "react-icons";
import { MdKeyboardArrowLeft } from "react-icons/md";
import BestekkenMap from "@/components/bestekken-map/bestekken-map";
import { HoofdcodeBestek } from "@/models/hoofdcode";
import { Collapse, Spinner, Row, Col, Container } from "react-bootstrap";
import HeaderedList from "@/components/headered-list/headered-list";
import BestekGeneralInfo from "./general_info/general_info";
import { Bepaling } from "@/models/bepalingen";
import useAPIDownload from "@/hooks/download.hook";
import { TruncatedBox } from "@/components/truncated-textbox/truncated-box";
import HighlightedText from "@/components/HighlightedText"; // Import the new component

interface Props {
    id?: number;
    reg?: string;
    modal?: boolean;
}

export default function BestekDetail(props: Props) {
    const navigate = useNavigate();
    const location = useLocation();
    let { id, reg } = useParams();
    if (props.modal) {
        id = props.id?.toString();
        reg = props.reg;   
    }

    const [ bestek, setBestek ] = useState<Bestek | null>(null);
    const [ isProcessing, setIsProcessing ] = useState<boolean>(false);
    const [ expandedHoofdcodes, setExpandedHoofdcodes ] = useState<string[]>([]);
    const [ expandedBepalingHoofdstukken, setExpandedBepalingHoofdstukken ] = useState<string[]>([]);
    
    // UPDATED: Initialize filter text (will be set from URL parameters)
    const [ hcFilterText, setHcFilterText ] = useState<string>("");
    const [ bepFilterText, setBepFilterText ] = useState<string>("");
    
    const downloadFromAPI = useAPIDownload();

    // NEW: Helper function to extract search terms from URL
    const getSearchTermsFromUrl = (location: any): string[] => {
        const urlParams = new URLSearchParams(location.search);
        const searchTerms = urlParams.get('searchTerms')?.split(',') || [];
        return searchTerms
            .map(term => term.trim())
            .filter(term => term.length > 0)
            .map(term => term.startsWith('"') && term.endsWith('"') && term.length > 2 ? term.slice(1, -1) : term);
    };

    // Extract search terms from URL for highlighting
    const searchTermsFromUrl = getSearchTermsFromUrl(location);

    useEffect(() => {
        if (bestek === null || bestek.id.toString() !== id) {
            apiService.get<Bestek>(`/bestek/${id}`)
                .then((response) => {
                    if (response.status === 200) {
                        setBestek(response.data);
                    } else if (response.status === 202) {
                        setIsProcessing(true);
                    }
                });
        }
    })

    // NEW: Extract search parameters from URL and auto-fill filters
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const searchTerms = urlParams.get('searchTerms')?.split(',') || [];
        const searchCategories = urlParams.get('searchCategories')?.split(',') || [];
        
        console.log('BestekDetail: URL search params detected:', {
            searchTerms,
            searchCategories
        });
        
        // Auto-fill filters based on search context
        if (searchTerms.length > 0) {
            const searchText = searchTerms
                .map(term => term.startsWith('"') && term.endsWith('"') && term.length > 2 ? term.slice(1, -1) : term)
                .join(' ');
            
            if (searchCategories.includes('hoofdcodes')) {
                // Set hoofdcode filter to search terms
                setHcFilterText(searchText);
                console.log('Auto-filled hoofdcode filter with:', searchText);
            }
            
            if (searchCategories.includes('bepalingen')) {
                // Set bepaling filter to search terms  
                setBepFilterText(searchText);
                console.log('Auto-filled bepaling filter with:', searchText);
            }
        }
    }, [location.search]);

    const formatHmInfo = (hm_info: { [weg: string]: number[] }): string => {
        let output: string[] = [];

        Object.keys(hm_info).forEach((weg) => {
            let hm = hm_info[weg];
            if (hm && hm.length > 0) {
                output.push(`${weg}: (${hm.join(', ')})`);
            }
        });

        return output.join(", ");
    }

    const mapping: { [category: string]: { label: string, items: { label: string, value: (b: Bestek) => string | JSX.Element }[] } } = {
        location: {
            label: "Locatie",
            items: [
                { label: "Tekst:", value: b => b.locatie?.raw ?? "" },
                { label: "Gemeente:", value: b => b.locatie?.gemeente ?? "" },
                { label: "Provincie:", value: b => b.locatie?.provincie ?? "" },
                { label: "Stad/dorp:", value: b => b.locatie?.woonplaats ?? "" },
                { label: "Straat:", value: b => (b.locatie?.straten ?? [""])[0]},
                { label: "(Rijks)wegen:", value: b => (b.locatie?.wegen ?? []).join(", ") },
                { label: "Hectometerinfo", value: b => formatHmInfo(b.locatie?.hm_info ?? {}) },
                { label: "Coördinaten:", value: b => {
                    return (
                        <>
                            {b.locatie?.coordinaten_ll?.lat} {b.locatie?.coordinaten_ll?.lon} 
                            {
                                b.locatie?.coordinaten_ll?.lat && 
                                <span>
                                    <span>&nbsp;</span>
                                    <a href={`https://www.google.com/maps/search/?api=1&query=${b.locatie.coordinaten_ll.lat},${b.locatie.coordinaten_ll.lon}`} target="_blank">Google Maps</a>
                                </span>
                            }
                        </>
                    )
                }},
                { label: "Rijksdriehoek:", value: b => <>{b.locatie?.coordinaten_rd?.x} {b.locatie?.coordinaten_rd?.y}</> }
            ]
        },
        ovk: {
            label: "Overeenkomsthouder",
            items: [
                { label: "Nummer:", value: b => b.overeenkomst_houder?.ovk_nummer ?? "" },
                { label: "Naam:", value: b => b.overeenkomst_houder?.ovk_naam ?? "" }
            ]
        }
    }

    // ENHANCED: Filter input with context indicators
    const renderFilterInput = (type: 'hoofdcode' | 'bepaling') => {
        const isHoofdcode = type === 'hoofdcode';
        const filterText = isHoofdcode ? hcFilterText : bepFilterText;
        const setFilterText = isHoofdcode ? setHcFilterText : setBepFilterText;
        const wasAutoFilled = isHoofdcode ? 
            new URLSearchParams(location.search).get('searchCategories')?.includes('hoofdcodes') :
            new URLSearchParams(location.search).get('searchCategories')?.includes('bepalingen');

        return (
            <div className="d-flex align-items-center gap-2">
                <input 
                    placeholder="Filter..." 
                    value={filterText} 
                    onChange={(e) => setFilterText(e.target.value)}
                    style={{
                        borderColor: wasAutoFilled && filterText ? '#0d6efd' : undefined,
                        borderWidth: wasAutoFilled && filterText ? '2px' : undefined,
                        flex: 1
                    }}
                />
                {wasAutoFilled && filterText && (
                    <small className="text-primary" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        Uit zoekresultaat
                    </small>
                )}
                {filterText && (
                    <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setFilterText("")}
                        style={{ fontSize: '0.75rem' }}
                    >
                        ✕
                    </button>
                )}
            </div>
        );
    };

    // ENHANCED: Improved hoofdcode filtering with case-insensitive search
    const sortedAndCombinedHoofdcodes: { [label: string]: { items: HoofdcodeBestek[] } } = (bestek?.hoofdcodes ?? [])
        .filter(hc => {
            if (hcFilterText.length === 0) {
                return true;
            } else {
                return hcFilterText.split(";").every(txt => {
                    txt = txt.trim();

                    if (txt.length === 3 && txt.split(":").length === 2) {
                        // Deficode filter
                        let [ pos, inh ] = txt.split(":").map(v => parseInt(v));
                        return hc.deficodes.some(dc => dc.positie === pos && dc.inhoud_nr === inh);
                    } else {
                        // ENHANCED: Case-insensitive search in multiple fields
                        const searchText = txt.toLowerCase();
                        return (
                            hc.hoofdcode.label?.toLowerCase().includes(searchText) || 
                            hc.hoofdcode.romptekst?.toLowerCase().includes(searchText) ||
                            hc.hoofdcode.hoofdtekst?.toLowerCase().includes(searchText) ||
                            hc.hoofdtekst?.toLowerCase().includes(searchText) || 
                            hc.overige_tekst?.toLowerCase().includes(searchText) || 
                            hc.bestekpost_nummer?.toLowerCase().startsWith(searchText) || 
                            hc.deficodes.some(dc => 
                                dc.beschrijving?.toLowerCase().includes(searchText) ||
                                dc.standaard_tekst?.toLowerCase().includes(searchText)
                            )
                        );
                    }
                })
            }
        })
        .sort((a, b) => a.hoofdcode.label < b.hoofdcode.label ? -1 : 1)
        .reduce(
            (reduced: { [label: string]: { items: HoofdcodeBestek[] } }, hoofdcode) => {
                if (hoofdcode.hoofdcode.label in reduced) {
                    reduced[hoofdcode.hoofdcode.label].items.push(hoofdcode);
                    reduced[hoofdcode.hoofdcode.label].items.sort((a, b) => parseInt(a.bestekpost_nummer) - parseInt(b.bestekpost_nummer));
                } else {
                    reduced[hoofdcode.hoofdcode.label] = { items: [hoofdcode] }
                }

                return reduced;
            }, {}
        );

    // ENHANCED: Improved bepaling filtering with multi-term support
    const bepalingOrig: Bepaling = JSON.parse(JSON.stringify(bestek?.bepaling ?? { hoofdstukken: [] })) as Bepaling;
    const bepaling: Bepaling = { hoofdstukken: [] } as Bepaling;
    const alphabeticalSort = (a: any, b: any) => a.nummer.localeCompare(b.nummer);
    
    const filterFunc = (i: any, ...fields: string[]) => {
        if (!bepFilterText || bepFilterText.length < 3) {
            return true;
        }

        // ENHANCED: Support multiple search terms (space-separated)
        const searchTerms = bepFilterText.toLowerCase().split(' ').filter(term => term.trim().length > 0);
        
        const result = fields.filter(f => f in i).map(f => {
            let val = i[f];

            if (typeof(val) === "string") {
                const fieldValue = val.toLowerCase();
                // All search terms must be found in at least one field
                return searchTerms.every(term => fieldValue.includes(term));
            } else {
                return true;
            }
        });

        return result.length === 0 || result.some(v => v === true);
    }

    bepaling.hoofdstukken = bepalingOrig.hoofdstukken.sort(alphabeticalSort).map(hoofdstuk => {
        hoofdstuk.paragrafen = hoofdstuk.paragrafen.sort(alphabeticalSort).map(paragraaf => {
            paragraaf.artikelen = paragraaf.artikelen.sort(alphabeticalSort).map(artikel => {
                artikel.leden = artikel.leden.filter(l => filterFunc(l, 'tekst')).sort(alphabeticalSort);
                return artikel;
            }).filter(a => a.leden.length > 0 || filterFunc(a, 'titel'));

            return paragraaf;
        }).filter(p => p.artikelen.length > 0 || filterFunc(p, 'titel'));

        return hoofdstuk;
    }).filter(h => h.paragrafen.length > 0 || filterFunc(h, 'titel'));

    // Auto-expand sections if they contain search results
    useEffect(() => {
        if (bepFilterText && bepaling.hoofdstukken.length === 1 && !expandedBepalingHoofdstukken.includes(bepaling.hoofdstukken[0].nummer)) {
            const codesCopy = [...expandedBepalingHoofdstukken];
            codesCopy.push(bepaling.hoofdstukken[0].nummer);
            setExpandedBepalingHoofdstukken(codesCopy);
        }
    }, [bepFilterText, bepaling.hoofdstukken.length]);

    const handleHoofdcodeClick = (label: string): void => {
        const codesCopy = [ ... expandedHoofdcodes ];
        const idx = codesCopy.indexOf(label);
        if (idx >= 0) {
            codesCopy.splice(idx, 1);
        } else {
            codesCopy.push(label);
        }
        setExpandedHoofdcodes(codesCopy);
    }

    const handleBepalingHoofdstukClick = (nummer: string): void => {
        const codesCopy = [ ... expandedBepalingHoofdstukken ];
        const idx = codesCopy.indexOf(nummer);
        if (idx >= 0) {
            codesCopy.splice(idx, 1);
        } else {
            codesCopy.push(nummer);
        }
        setExpandedBepalingHoofdstukken(codesCopy);
    }

    const downloadBestek = (bestek: Bestek): void => {
        downloadFromAPI(
            { method: 'get', path: `/bestek/download/${bestek.id}`, data: {} },
            `${bestek.registratie_nummer}.rsx`,
            { error: () => alert('Download mislukt') }
        );
    };

    return (
        <div>
            { !props.modal && (
                <div className={styles.header}>
                    <IconContext.Provider value={{size: '40px'}}><MdKeyboardArrowLeft onClick={() => navigate(-1)} /></IconContext.Provider>
                    <div className={styles.header_title}>Bestek met registratienummer {reg}</div>
                    <div style={{flexGrow: 1}}></div>
                    <div className={styles.download_button} onClick={() => downloadBestek(bestek as Bestek)}>Download</div>
                </div>
            )} 

            <div className={styles.main_container}>
            {
                bestek === null && !isProcessing && (
                    <div className={styles.loader}>
                        <Spinner animation="border" size="sm"></Spinner>
                        <span className="mt-2">Bestek details ophalen...</span>
                    </div>
                )
            }

            {
                isProcessing && (
                    <div className={styles.loader}>
                        <span className="mt-2">Het bestek is niet volledig verwerkt. Probeer het later nog eens.</span>
                    </div>
                )
            }

            {
                bestek && (
                    <div className={styles.main}>
                        <Container fluid>
                            <Row>
                                <Col sm={12} md={6}>
                                    <BestekGeneralInfo bestek={bestek} />
                                    {
                                        Object.keys(mapping).map(k => {
                                            return (
                                                <HeaderedList key={k} title={mapping[k].label} items={mapping[k].items.map(i => { return { label: i.label, value: i.value(bestek) }})}></HeaderedList>
                                            )
                                        })
                                    }
                                </Col>

                                <Col sm={12} md={6}>
                                    {
                                        bestek && bestek.locatie?.coordinaten_ll?.lat && <BestekkenMap bestekken={[bestek]} allowHighlight={false} showPopup={false} minPaddingKM={5} />
                                    }

                                    <div className={styles.main_partial}>
                                        <div className={styles.main_partial_header}>
                                            <h6>Hoofdcodes</h6>
                                            {/* UPDATED: Use enhanced filter input */}
                                            {renderFilterInput('hoofdcode')}
                                        </div>
                                        <div className={`d-flex flex-column ${styles.hoofdcode_list_container}`}>
                                        {
                                            Object.keys(sortedAndCombinedHoofdcodes).length === 0 && <span>Er zijn geen hoofdcodes gevonden die voldoen aan het zoekcriterium.</span>
                                        }
                                        {
                                            Object.values(sortedAndCombinedHoofdcodes).map((item, idx) => {
                                                const hc = item.items[0];
                                                // Get search terms for highlighting
                                                const currentSearchTerms = hcFilterText ? hcFilterText.split(' ') : searchTermsFromUrl;

                                                return (
                                                    <div key={hc.id + "-" + idx} className={`${styles.hoofdcode_container} ${expandedHoofdcodes.includes(hc.hoofdcode.label) ? styles.hoofdcode_container_sticky : ''}`}>
                                                        <div onClick={() => handleHoofdcodeClick(hc.hoofdcode.label)}>
                                                            <span>
                                                                <HighlightedText 
                                                                    text={`${hc.hoofdcode.label} - ${hc.hoofdcode.romptekst ?? hc.hoofdcode.hoofdtekst}`}
                                                                    searchTerms={currentSearchTerms}
                                                                />
                                                                <span style={{fontStyle: 'italic'}}> ({item.items.length}x)</span>
                                                            </span><br />
                                                        </div>
                                                        
                                                        <Collapse in={expandedHoofdcodes.includes(hc.hoofdcode.label)}>
                                                            <div>
                                                                {
                                                                    item.items.map(i => {
                                                                        return (
                                                                            <div key={i.id} className={styles.bestekpostnr_container}>
                                                                                <div className={styles.nr_hoev_container}>
                                                                                    <div>Bestekpostnr. {i.bestekpost_nummer}</div>
                                                                                    {/* Hoeveelheid */}
                                                                                    <div className={styles.bestekpostnr_hoeveelheid_container}>Hoev.: {i.hoeveelheid ?? 0}{i.hoofdcode.eenheid}</div>
                                                                                </div>
                                                                                
                                                                                <div className={styles.bestekpostnr_detail}>
                                                                                    {/* Titel + volledige tekst - WITH HIGHLIGHTING */}
                                                                                    { i.hoofdtekst && i.hoofdtekst.length > 0 && (
                                                                                        <div className={styles.hoofdcode_hoofdtekst}>
                                                                                            <HighlightedText 
                                                                                                text={i.hoofdtekst} 
                                                                                                searchTerms={currentSearchTerms}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    { i.overige_tekst && i.overige_tekst.length > 0 && (
                                                                                        <div>
                                                                                            <HighlightedText 
                                                                                                text={i.overige_tekst} 
                                                                                                searchTerms={currentSearchTerms}
                                                                                            />
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Deficodes - WITH HIGHLIGHTING */}
                                                                                    {
                                                                                        i.deficodes.length > 0 && (
                                                                                            <>
                                                                                                <span>Deficodes:</span><br />
                                                                                                <div className={styles.deficode_list}>
                                                                                                    <table>
                                                                                                    {
                                                                                                        i.deficodes.map((code, codeIdx) => {
                                                                                                            return (
                                                                                                                <tr key={codeIdx}>
                                                                                                                    <td style={{ width: '40px' }}>{code.positie}:{code.inhoud_nr}</td> 
                                                                                                                    <td>
                                                                                                                        <HighlightedText 
                                                                                                                            text={code.beschrijving ?? code.standaard_tekst ?? ''} 
                                                                                                                            searchTerms={currentSearchTerms}
                                                                                                                        />
                                                                                                                        {code.hoeveelheid && (
                                                                                                                            <>
                                                                                                                                <br />
                                                                                                                                <span>Hoeveelheid: {code.hoeveelheid} {code.eenheid}</span>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            )
                                                                                                        })
                                                                                                    }
                                                                                                    </table>
                                                                                                </div>
                                                                                            </>
                                                                                        )
                                                                                    }
                                                                                    
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    })
                                                                }
                                                            </div>
                                                        </Collapse>
                                                    </div>
                                                )
                                            })
                                        }
                                        </div>
                                    </div>

                                    <div className={styles.main_partial}>
                                        <div className={styles.main_partial_header}>
                                            <h6>Bepalingen</h6>
                                            {/* UPDATED: Use enhanced filter input */}
                                            {renderFilterInput('bepaling')}
                                        </div>
                                        <div className={`d-flex flex-column ${styles.bepaling_container}`}>
                                        {
                                            bepaling.hoofdstukken.length === 0 && <span>Er zijn geen bepalingen gevonden die voldoen aan het zoekcriterium.</span>
                                        }
                                        {
                                            bepaling.hoofdstukken.map((item, idx) => {
                                                // Get search terms for highlighting
                                                const currentSearchTerms = bepFilterText ? bepFilterText.split(' ') : searchTermsFromUrl;

                                                return (
                                                    <div key={item.nummer + "-" + idx} className={`${styles.bepaling_hoofdstuk_container} ${expandedBepalingHoofdstukken.includes(item.nummer) ? styles.bepaling_hoofdstuk_container_sticky_incl_children : ''}`}>
                                                        {/* Hoofdstuk titel - WITH HIGHLIGHTING */}
                                                        <div onClick={() => handleBepalingHoofdstukClick(item.nummer)}>
                                                            <span>
                                                                {item.nummer} - <HighlightedText 
                                                                    text={item.titel} 
                                                                    searchTerms={currentSearchTerms}
                                                                />
                                                            </span><br />
                                                        </div>
                                                        
                                                        <Collapse in={expandedBepalingHoofdstukken.includes(item.nummer)}>
                                                            <div>
                                                                {
                                                                    item.paragrafen.map((par, parIdx) => {
                                                                        return (
                                                                            <div key={parIdx} className={styles.paragraaf_container}>
                                                                                {/* Paragraaf titel - WITH HIGHLIGHTING */}
                                                                                <div>
                                                                                    {par.nummer} - <HighlightedText 
                                                                                        text={par.titel} 
                                                                                        searchTerms={currentSearchTerms}
                                                                                    />
                                                                                </div>
                                                                                <div className={styles.paragraaf_artikelen_container}>
                                                                                    {
                                                                                        par.artikelen.map((art, artIdx) => {
                                                                                            return (
                                                                                                <div key={artIdx} className={styles.artikel_container}>
                                                                                                    {/* Artikel titel - WITH HIGHLIGHTING */}
                                                                                                    <div>
                                                                                                        {art.nummer} - <HighlightedText 
                                                                                                            text={art.titel} 
                                                                                                            searchTerms={currentSearchTerms}
                                                                                                        />
                                                                                                    </div>
                                                                                                    <div className={styles.artikel_leden_container}>
                                                                                                    {
                                                                                                        art.leden.map((lid, lidIdx) => {
                                                                                                            return (
                                                                                                                <div key={lidIdx} className={styles.lid_container}>
                                                                                                                    <div><div className={styles.lid_nummer}>{lid.nummer}</div></div>
                                                                                                                    {/* Lid tekst - WITH HIGHLIGHTING */}
                                                                                                                    <div>
                                                                                                                        <HighlightedText 
                                                                                                                            text={lid.tekst} 
                                                                                                                            searchTerms={currentSearchTerms}
                                                                                                                        />
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            )
                                                                                                        })
                                                                                                    }
                                                                                                    </div>
                                                                                                </div>
                                                                                            )
                                                                                        })
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    })
                                                                }
                                                            </div>
                                                        </Collapse>
                                                    </div>
                                                )
                                            })
                                        }
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                )
            }
            </div>
        </div>
    )
}